import React, { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { ZenProvider } from "./contexts/ZenContext";
import BackgroundLoop from "./components/BackgroundLoop";
import Header from "./components/Header";
import AudioPlayer from "./components/AudioPlayer";
import AmbientMixer from "./components/AmbientMixer";
import TrackList from "./components/TrackList";
import YouTubeSection from "./components/YouTubeSection";
import BreathingMode from "./components/BreathingMode";
import BodyScanMode from "./components/BodyScanMode";
import SplashScreen from "./components/SplashScreen";
import AdSettings from "./components/AdSettings";
import InstallPWA from "./components/InstallPWA";
import AndroidBackButton from "./components/AndroidBackButton";
import { BetaTesterPopup } from "./components/BetaTesterPromo";
import { isIosApp } from "./utils/platform";
// Heavy / secondary routes — lazy-loaded for faster initial bundle
const VoicePreviewPage = lazy(() => import("./components/VoicePreviewPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const DonationsPage = lazy(() => import("./pages/DonationsPage"));
const BetaTesterLanding = lazy(() => import("./BetaTesterLanding"));
const BetaTestersAdminPage = lazy(() => import("./pages/BetaTestersAdminPage"));
import { DonationsAdminContext } from "./pages/DonationsPage";
import { Music, Youtube, Headphones, Wind, User, TrendingUp, Sparkles, Flower2, StretchHorizontal, Users, BookOpen, UserCircle, HelpCircle, MessageCircle, ArrowRight, Gift, Star, Film, Heart, Trophy } from "lucide-react";
import ShareButton from "./components/ShareButton";
import BookingButton from "./components/BookingButton";
import StatsPanel from "./components/StatsPanel";
import AffirmationsPanel from "./components/AffirmationsPanel";
import YogaPanel from "./components/YogaPanel";
import StretchingPanel from "./components/StretchingPanel";
import SportMentalPanel from "./components/SportMentalPanel";
import useAppSettings from "./hooks/useAppSettings";
import AdBanner from "./components/AdBanner";
import BetaTesterBanner from "./components/BetaTesterBanner";
import InAppBrowserBanner from "./components/InAppBrowserBanner";
import { getDailyQuote } from "./data/dailyQuotes";
import { preloadDefaults, preloadAllMedia } from "./utils/mediaPreloader";
import { initAdMob, showBottomBanner, prepareInterstitial, initAppOpenAds, BANNER_ENABLED } from "./services/admob";
import { precacheVoices } from "./services/voicePrecache";

// Context pour gérer les modals fullscreen
const ModalContext = createContext();

export const useModal = () => useContext(ModalContext);

const TABS = [
  { id: 'audio', label: 'Audios', icon: Music },
  { id: 'youtube', label: 'YouTube', icon: Youtube },
];

const API = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [activeTab, setActiveTab] = useState('audio');
  const [showAdSettings, setShowAdSettings] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showAffirmations, setShowAffirmations] = useState(false);
  const [showYoga, setShowYoga] = useState(false);
  const [showStretching, setShowStretching] = useState(false);
  const [showSportMental, setShowSportMental] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [visitCount, setVisitCount] = useState(0);
  const { openBreathing, openBodyScan, setIsAdminGlobal } = useModal();
  const { settings: appSettings } = useAppSettings();

  useEffect(() => {
    // Record visit and get count
    fetch(`${API}/api/visit`, { method: 'POST' })
      .then(r => r.json())
      .then(d => setVisitCount(d.total))
      .catch(() => {
        fetch(`${API}/api/visit-count`).then(r => r.json()).then(d => setVisitCount(d.total)).catch(() => {});
      });
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <BackgroundLoop backgroundIndex={0} />
      <Header onAdminAccess={() => { setShowAdSettings(true); setIsAdmin(true); setIsAdminGlobal && setIsAdminGlobal(true); }} />
      
      {/* Ad Settings Modal */}
      <AdSettings isOpen={showAdSettings} onClose={() => setShowAdSettings(false)} />
      <StatsPanel isOpen={showStats} onClose={() => setShowStats(false)} />
      <AffirmationsPanel isOpen={showAffirmations} onClose={() => setShowAffirmations(false)} isAdmin={isAdmin} />
      <YogaPanel isOpen={showYoga} onClose={() => setShowYoga(false)} isAdmin={isAdmin} />
      <StretchingPanel isOpen={showStretching} onClose={() => setShowStretching(false)} isAdmin={isAdmin} />
      <SportMentalPanel isOpen={showSportMental} onClose={() => setShowSportMental(false)} isAdmin={isAdmin} />
      {!isIosApp() && <BetaTesterPopup />}
      
      <main className="main-content pt-20 pb-12 px-4 page-enter">
        <div className="max-w-md mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-semibold text-white mb-3 tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Hypnose <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 bg-clip-text text-transparent">clé</span>
            </h1>
            <p className="text-zinc-400 text-sm sm:text-base max-w-sm mx-auto leading-relaxed">
              Votre compagnon de relaxation et de bien-être
            </p>
          </div>

          {/* Daily Quote */}
          <div className="text-center mb-6 px-4">
            <p className="text-zinc-300/70 italic text-sm leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
              "{getDailyQuote()}"
            </p>
          </div>

          {/* Visitor Counter */}
          {visitCount > 0 && (
            <div className="flex justify-center mb-6" data-testid="visitor-counter">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/40 backdrop-blur-md border border-amber-500/15">
                <Users className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-amber-400/90 text-xs font-medium">{visitCount.toLocaleString('fr-FR')}</span>
                <span className="text-zinc-500 text-xs">utilisateur{visitCount > 1 ? 's' : ''}</span>
              </div>
            </div>
          )}

          {/* Beta Tester Invitation */}
          <BetaTesterBanner />

          {/* Action Grid - Glassmorphism Cards with colored gradients + gold halo */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              onClick={openBreathing}
              className="flex flex-col items-center justify-center p-5 rounded-3xl backdrop-blur-2xl bg-gradient-to-br from-amber-500/15 to-cyan-500/15 border border-amber-500/20 text-center gap-2.5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(245,158,11,0.25)] hover:-translate-y-0.5 active:scale-95 group shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
              data-testid="breathing-mode-btn"
            >
              <Wind className="w-7 h-7 text-amber-400 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-white">Respiration Guidée</span>
            </button>

            <button
              onClick={openBodyScan}
              className="flex flex-col items-center justify-center p-5 rounded-3xl backdrop-blur-2xl bg-gradient-to-br from-purple-500/15 to-pink-500/15 border border-amber-500/20 text-center gap-2.5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(245,158,11,0.25)] hover:-translate-y-0.5 active:scale-95 group shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
              data-testid="body-scan-mode-btn"
            >
              <User className="w-7 h-7 text-purple-400 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-white">Scan Corporel</span>
            </button>

            <button
              onClick={() => setShowAffirmations(true)}
              className="flex flex-col items-center justify-center p-5 rounded-3xl backdrop-blur-2xl bg-gradient-to-br from-pink-500/15 to-amber-500/15 border border-amber-500/20 text-center gap-2.5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(245,158,11,0.25)] hover:-translate-y-0.5 active:scale-95 group shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
              data-testid="affirmations-btn"
            >
              <Sparkles className="w-7 h-7 text-pink-400 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-white">Affirmations</span>
            </button>

            <button
              onClick={() => setShowStretching(true)}
              className="flex flex-col items-center justify-center p-5 rounded-3xl backdrop-blur-2xl bg-gradient-to-br from-cyan-500/15 to-blue-500/15 border border-amber-500/20 text-center gap-2.5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(245,158,11,0.25)] hover:-translate-y-0.5 active:scale-95 group shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
              data-testid="stretching-btn"
            >
              <StretchHorizontal className="w-7 h-7 text-cyan-400 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-white">Étirements</span>
            </button>

            {!isIosApp() && (
            <button
              onClick={() => setShowYoga(true)}
              className="flex flex-col items-center justify-center p-5 rounded-3xl backdrop-blur-2xl bg-gradient-to-br from-emerald-500/15 to-teal-500/15 border border-amber-500/20 text-center gap-2.5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(245,158,11,0.25)] hover:-translate-y-0.5 active:scale-95 group shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
              data-testid="yoga-btn"
            >
              <Flower2 className="w-7 h-7 text-emerald-400 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-white">Yoga</span>
            </button>
            )}

            <button
              onClick={() => setShowStats(true)}
              className="flex flex-col items-center justify-center p-5 rounded-3xl backdrop-blur-2xl bg-gradient-to-br from-orange-500/15 to-amber-500/15 border border-amber-500/20 text-center gap-2.5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(245,158,11,0.25)] hover:-translate-y-0.5 active:scale-95 group shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
              data-testid="stats-btn"
            >
              <TrendingUp className="w-7 h-7 text-orange-400 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-white">Mes Stats</span>
            </button>

            {(isAdmin || appSettings.sport_mental_visible) && (
              <button
                onClick={() => setShowSportMental(true)}
                className="flex flex-col items-center justify-center p-5 rounded-3xl backdrop-blur-2xl bg-gradient-to-br from-red-500/15 to-orange-500/15 border border-amber-500/20 text-center gap-2.5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(245,158,11,0.25)] hover:-translate-y-0.5 active:scale-95 group shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
                data-testid="sport-mental-btn"
              >
                <Trophy className="w-7 h-7 text-orange-300 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium text-white">Prépa Mentale</span>
              </button>
            )}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col items-center gap-3 mb-6">
            <ShareButton />
            <BookingButton />
          </div>

          {/* Tab Navigation - Gold styled */}
          <div className="flex p-1 bg-black/50 backdrop-blur-md rounded-full border border-white/10 mb-6">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                    isActive
                      ? (tab.id === 'youtube'
                        ? 'bg-gradient-to-r from-amber-600 to-amber-500 text-black shadow-[0_0_15px_rgba(245,158,11,0.35)]'
                        : 'bg-zinc-800 text-white shadow-md')
                      : 'text-zinc-400 hover:text-zinc-200'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Main Content */}
          <div>
            {/* Audio player + ambient sounds remain always visible (usable from YouTube tab too) */}
            <div className="space-y-6">
              <AudioPlayer />
              <AmbientMixer />
            </div>

            {/* Tab-specific content below */}
            <div className="mt-6">
              {activeTab === 'audio' && <TrackList />}
              {activeTab === 'youtube' && <YouTubeSection />}
            </div>
          </div>

          {/* Aura Health Banner - Promote 50+ guided sessions (masqué sur iOS) */}
          {!isIosApp() && (
          <a
            href="https://www.aurahealth.io/coaches/colot-ludovic"
            target="_blank"
            rel="noreferrer"
            data-testid="aura-banner"
            className="mt-8 group relative block overflow-hidden rounded-2xl border border-violet-400/30 bg-gradient-to-br from-violet-600/20 via-indigo-700/20 to-amber-500/15 p-5 sm:p-6 hover:border-violet-300/60 transition shadow-lg shadow-violet-500/10"
          >
            {/* Decorative glow */}
            <div className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-400/20 blur-3xl group-hover:bg-amber-400/30 transition" />
            <div className="pointer-events-none absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-violet-400/20 blur-3xl" />

            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-400 to-indigo-600 flex items-center justify-center text-2xl shadow-lg shadow-violet-500/30">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30">
                    Aura Health
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-emerald-400/20 text-emerald-300 border border-emerald-400/40 animate-pulse">
                    <Gift className="w-3 h-3" /> 1 mois offert
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-heading text-white mb-1 leading-tight">
                  Votre <span className="text-amber-300">pass Aura gratuit</span> 1 mois
                </h3>
                <p className="text-sm text-white/70 leading-relaxed mb-1">
                  Accès illimité à <span className="text-amber-200/90 font-medium">50+ séances guidées</span> par Ludovic — hypnoses, méditations, histoires apaisantes
                </p>
                <p className="text-xs text-white/50">
                  13 000+ écoutes · 290+ abonnés · Sans engagement
                </p>
              </div>
              <div className="shrink-0 hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400/20 group-hover:bg-amber-400/40 border border-amber-300/40 group-hover:border-amber-300/70 transition text-sm font-medium text-amber-100">
                Activer
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
            <div className="relative sm:hidden mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400/20 border border-amber-300/40 text-sm font-semibold text-amber-100">
              Activer mon pass gratuit <ArrowRight className="w-4 h-4" />
            </div>
          </a>
          )}

          {/* Footer */}
          <div className="mt-10 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/40 backdrop-blur-md border border-white/5">
              <Headphones className="w-4 h-4 text-amber-500" />
              <span className="text-zinc-500 text-xs">
                Utilisez des écouteurs pour une meilleure expérience
              </span>
            </div>
          </div>

          {/* Discover content links - SEO + AdSense friendly */}
          <div className="mt-8 grid grid-cols-4 sm:grid-cols-7 gap-2.5">
            {!isIosApp() && (
            <Link to="/dons" data-testid="home-link-dons"
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-pink-500/10 border border-pink-500/30 hover:bg-pink-500/20 hover:border-pink-400/60 transition group">
              <Heart className="w-5 h-5 text-pink-400 group-hover:scale-110 transition" />
              <span className="text-xs font-medium text-pink-200">Soutenir</span>
            </Link>
            )}
            <a href="/blog" data-testid="home-link-blog"
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-white/[0.03] border border-amber-500/15 hover:bg-amber-500/10 hover:border-amber-400/40 transition group">
              <BookOpen className="w-5 h-5 text-amber-400 group-hover:scale-110 transition" />
              <span className="text-xs font-medium text-white/80">Blog</span>
            </a>
            <a href="/livre-d-or" data-testid="home-link-reviews"
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-white/[0.03] border border-amber-500/15 hover:bg-amber-500/10 hover:border-amber-400/40 transition group">
              <Star className="w-5 h-5 text-amber-400 group-hover:scale-110 transition" />
              <span className="text-xs font-medium text-white/80">Livre d'or</span>
            </a>
            <a href="/galerie" data-testid="home-link-gallery"
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-white/[0.03] border border-amber-500/15 hover:bg-amber-500/10 hover:border-amber-400/40 transition group">
              <Film className="w-5 h-5 text-amber-400 group-hover:scale-110 transition" />
              <span className="text-xs font-medium text-white/80">Galerie</span>
            </a>
            <a href="/a-propos" data-testid="home-link-about"
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-white/[0.03] border border-amber-500/15 hover:bg-amber-500/10 hover:border-amber-400/40 transition group">
              <UserCircle className="w-5 h-5 text-amber-400 group-hover:scale-110 transition" />
              <span className="text-xs font-medium text-white/80">À propos</span>
            </a>
            <a href="/faq" data-testid="home-link-faq"
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-white/[0.03] border border-amber-500/15 hover:bg-amber-500/10 hover:border-amber-400/40 transition group">
              <HelpCircle className="w-5 h-5 text-amber-400 group-hover:scale-110 transition" />
              <span className="text-xs font-medium text-white/80">FAQ</span>
            </a>
            <a href="/contact" data-testid="home-link-contact"
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-white/[0.03] border border-amber-500/15 hover:bg-amber-500/10 hover:border-amber-400/40 transition group">
              <MessageCircle className="w-5 h-5 text-amber-400 group-hover:scale-110 transition" />
              <span className="text-xs font-medium text-white/80">Contact</span>
            </a>
            {!isIosApp() && (
            <button onClick={() => window.dispatchEvent(new Event('hypnose-open-support'))} data-testid="home-link-support"
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-amber-500/15 border border-amber-400/40 hover:bg-amber-500/25 hover:border-amber-300/60 transition group">
              <Heart className="w-5 h-5 text-amber-300 group-hover:scale-110 transition" />
              <span className="text-xs font-semibold text-amber-100">Soutenir l&apos;appli</span>
            </button>
            )}
          </div>

          {/* Banner Ad */}
          <div className="mt-6">
            <AdBanner format="auto" className="rounded-xl overflow-hidden" />
          </div>
        </div>
      </main>
    </div>
  );
};

// Composant qui gère les modals en dehors du Router
const ModalManager = ({ showBreathing, showBodyScan, closeBreathing, closeBodyScan, isAdmin }) => {
  // Show an interstitial when the user finishes a session (modal close).
  // Imported lazily to avoid coupling with native bundle on web.
  const wrapClose = (origClose) => async () => {
    try {
      const { showInterstitial } = await import('./services/admob');
      showInterstitial(); // no-op on web
    } catch (_) {}
    origClose();
  };
  return (
    <>
      {showBreathing && <BreathingMode onClose={wrapClose(closeBreathing)} isAdmin={isAdmin} />}
      {showBodyScan && <BodyScanMode onClose={wrapClose(closeBodyScan)} isAdmin={isAdmin} />}
    </>
  );
};

function App() {
  const [showBreathing, setShowBreathing] = useState(false);
  const [showBodyScan, setShowBodyScan] = useState(false);
  // Skip splash on content pages (blog, a-propos, faq, contact, voix-coach, livre-d-or, galerie, dons, admin, beta-tester)
  const isContentPage = typeof window !== 'undefined' && /^\/(blog|a-propos|faq|contact|voix-coach|livre-d-or|galerie|dons|soutenir|admin|beta)/.test(window.location.pathname);
  const [showSplash, setShowSplash] = useState(!isContentPage);
  // Admin mode is session-only: must be re-activated via 5 logo taps on each
  // page load. We intentionally do NOT persist it to localStorage so the
  // "Éditer les méthodes" button never leaks to regular visitors.
  const [isAdminGlobal, setIsAdminGlobal] = useState(false);

  // Clean up any legacy persisted admin flag from previous versions
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('hypnose_admin_global');
    }
  }, []);

  // Initialize AdMob (Capacitor native only). No-op on web — AdSense
  // keeps handling the browser version unchanged.
  useEffect(() => {
    (async () => {
      // Reserve banner space on ALL native screens, but only when a banner
      // is actually configured (real unit or testing mode).
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform?.() && BANNER_ENABLED) document.body.classList.add('native-app');
      } catch (_) {}
      await initAdMob();
      // Show bottom banner after a short delay so the splash gets uninterrupted
      setTimeout(() => { showBottomBanner(); }, 2500);
      // Pre-warm an interstitial in the background
      prepareInterstitial();
      // iOS only: App Open ad at launch + on foreground resume
      initAppOpenAds();
      // Download all Body Scan + Breathing voices into the local cache for
      // instant, fluid playback (runs after boot to keep startup snappy)
      setTimeout(() => { precacheVoices(); }, 8000);
    })();
  }, []);

  const modalContextValue = {
    openBreathing: () => setShowBreathing(true),
    openBodyScan: () => setShowBodyScan(true),
    closeBreathing: () => setShowBreathing(false),
    closeBodyScan: () => setShowBodyScan(false),
    setIsAdminGlobal,
  };

  // Afficher le splash screen au démarrage
  if (showSplash) {
    return (
      <SplashScreen onComplete={() => {
        setShowSplash(false);
        // Pré-charger les fonds par défaut immédiatement, puis tout le reste en arrière-plan
        preloadDefaults().then(() => preloadAllMedia());
      }} />
    );
  }

  return (
    <ModalContext.Provider value={modalContextValue}>
      <div className="App">
        <ZenProvider>
          <BrowserRouter>
            <AndroidBackButton />
            <InAppBrowserBanner />
            <Suspense fallback={<RouteFallback />}>
              <DonationsAdminContext.Provider value={isAdminGlobal}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/voix-coach" element={<VoicePreviewPage />} />
                  <Route path="/a-propos" element={<AboutPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/blog" element={<BlogListPage />} />
                  <Route path="/blog/:slug" element={<ArticlePage />} />
                  <Route path="/livre-d-or" element={<ReviewsPage />} />
                  <Route path="/galerie" element={<GalleryPage />} />
                  <Route path="/dons" element={isIosApp() ? <Navigate to="/" replace /> : <DonationsPage />} />
                  <Route path="/soutenir" element={isIosApp() ? <Navigate to="/" replace /> : <DonationsPage />} />
                  <Route path="/beta-tester" element={<BetaTesterLanding />} />
                  <Route path="/beta" element={<BetaTesterLanding />} />
                  <Route path="/admin/beta-testers" element={<BetaTestersAdminPage />} />
                </Routes>
              </DonationsAdminContext.Provider>
            </Suspense>
          </BrowserRouter>
          <Toaster position="bottom-center" richColors />
          
          {/* PWA Install Banner */}
          <InstallPWA />
        </ZenProvider>
        
        {/* Modals rendus EN DEHORS de tout - complètement isolés */}
        <ModalManager 
          showBreathing={showBreathing}
          showBodyScan={showBodyScan}
          closeBreathing={() => setShowBreathing(false)}
          closeBodyScan={() => setShowBodyScan(false)}
          isAdmin={isAdminGlobal}
        />
      </div>
    </ModalContext.Provider>
  );
}

// Lightweight loader for lazy routes (page-level)
function RouteFallback() {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
    </div>
  );
}

export default App;
